// @ts-strict-ignore
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import React from 'react';
import { AppComponentProps, QuickStartCISetupStep, QuickStartCISetupViewProps } from '../../../typescriptHelpers';
import { InfoTip, setupModuleStyle } from '../../setup/shared';
import { SettingOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useForm } from 'antd/lib/form/Form';
import Form from 'antd/lib/form';
import { quickStartCIFieldsFormDataForIntegration } from '../../../helpers/integrationFormHelpers';
import { selectOrgProviders, selectVCSIntegration } from '../../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { renderFormFields } from '../../../helpers/formHelpers';
import { RootState } from '../../../reducers/reducers';
import { UpdateIntegrationRequest, tryUpdateIntegrationRequest } from '../../../actions/integrations';
import { integrationUpdatableForIntegrationType } from '../../../helpers/integrationHelpers';
import { globalMessage } from '../../../utils/globalMessage';
import { QuestionsInfoSection } from '../QuestionsInfoSection';
import { GitHubVCSIntegrationData } from '../../../types';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    const [form] = useForm();
    const dispatch = useDispatch();

    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));

    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, orgId));
    const provider = integrationProviders.find((provider) => provider.id === vcsIntegration?.providerId);

    const fields = vcsIntegration ? quickStartCIFieldsFormDataForIntegration(vcsIntegration, provider) : [];

    const initialValues = fields.reduce((acc, field) => {
        acc[field.name] = field.initialValue;
        return acc;
    }, {});

    const integrationId = vcsIntegration?.id;
    const integrationInstallationId = vcsIntegration?.installationId;
    const updatableType = integrationUpdatableForIntegrationType(integrationId);

    const updateIntegration = (values) => {
        const updateRequest: UpdateIntegrationRequest = {
            orgId: orgId,
            appId: appId,
            integrationId: integrationInstallationId,
            data: {
                repoName: values?.repoName,
            },
        };

        if (updatableType) {
            dispatch(tryUpdateIntegrationRequest(updatableType, updateRequest));
        } else {
            globalMessage.error('This integration does not support updating.');
        }
    };

    const updateIntegrationIsLoading = useIsLoading(updatableType, integrationInstallationId);
    const updateIntegrationSuccess = useUpdated(updatableType, integrationInstallationId);
    const updateIntegrationError = useError(updatableType, integrationInstallationId);

    React.useEffect(() => {
        if (updateIntegrationSuccess) {
            globalMessage.success(`Repository name successfully saved!`);
        }
    }, [updateIntegrationSuccess]);

    React.useEffect(() => {
        if (updateIntegrationError) {
            globalMessage.error(updateIntegrationError);
        }
    }, [updateIntegrationError]);

    return (
        <div
            style={{
                ...setupModuleStyle,
            }}
        >
            <Form form={form} onFinish={updateIntegration} initialValues={initialValues}>
                <Space direction="vertical" style={{ width: '100%' }} size={20}>
                    {renderFormFields(fields)}

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                            {() => (
                                <Button
                                    loading={updateIntegrationIsLoading}
                                    type="primary"
                                    htmlType="submit"
                                    icon={<ArrowRightOutlined />}
                                >
                                    Save
                                </Button>
                            )}
                        </Form.Item>
                    </div>
                </Space>
            </Form>
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>We’ll need you to select the GitHub repo Runway should use to configure your workflow.</InfoTip>
            <QuestionsInfoSection />
        </Space>
    );
};

export const VCSSetup: QuickStartCISetupStep = {
    icon: () => SettingOutlined,
    headerTitle: 'Choose a repository.',
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: (state, { appId }) => {
        const vcsIntegration = selectVCSIntegration(state, appId);
        return !!(vcsIntegration?.data as GitHubVCSIntegrationData)?.repoName;
    },
};
